<template>
  <div class="net-input-text" ref="mainElement" :class="{ '-editing': editing, '-stylable': stylable, '-disabled': disabled }">
    <editor-content
      v-if="editing && !disabled"
      ref="editorContentElement"
      class="net-input-text__content ignore-drag"
      :editor="(editor as Editor)"
      @keydown.esc="leaveEditMode()"
      @pointerdown="onTextPointerDown"
    />
    <template v-else>
      <button
        v-if="stylable"
        ref="buttonElement"
        class="net-input-text__rendered"
        @focus="onViewFocus"
        @blur="onViewBlur"
        @pointerdown="onViewMousedown"
        @keydown.enter="switchToEditMode"
      >
        <slot>
          <formatted-text
            tag="div"
            :value="value || placeholder"
            :class="{ 'is-empty': isEmpty(value) }"
            :disabled="disabled"
          />
        </slot>
      </button>
      <button
        v-else
        ref="buttonElement"
        class="net-input-text__rendered"
        :class="{ 'is-empty': !value }"
        :disabled="disabled"
        @focus="onViewFocus"
        @blur="onViewBlur"
        @pointerdown="onViewMousedown"
        @keydown.enter="switchToEditMode"
      >
        <slot>
          <span class="net-input-text__wrapped-preview">
            {{ renderedValue || value || placeholder }}
          </span>
        </slot>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * A WYSIWYG text editor.
 */
import { ref, watch, computed, toRef } from "vue";
import { v4 as uuid } from "uuid";

import { isEmpty } from "@/helpers/utility/Text";

// Import the editor
import {
  Editor,
  EditorContent,
  useEditor
} from "@tiptap/vue-3";

import { useNetTextToolBarContext } from "./netTextToolbarContext";
import { insertCaretAtPoint } from "@/helpers/utility/Range";

import { Extension } from "@tiptap/core";
import { minimalExtensions, fullExtensions } from "@core/input/TextEditorExtensions";

import FormattedText from "@core/ui/FormattedText.vue";

import type { EditorEvents, EditorOptions } from "@tiptap/core";
import type { Handleable } from "@core/types/Events";
import type { TextOptions } from "./netTextToolbarContext";

defineOptions({
  name: "NetInputText"
});

interface Props {
  id?: string;
  value: string;
  renderedValue?: string;
  placeholder?: string;
  stylable?: boolean;
  editorClass?: string;
  maxLines?: number;
  lineHeight?: number;
  disabled?: boolean;
  selected?: boolean;
  position?: { x: number; y: number };
  textOptions?: TextOptions;
}
const props = withDefaults(defineProps<Props>(), {
  id: () => `input-${uuid()}`,
  value: "",
  placeholder: "",
  stylable: false,
  editorClass: "",
  maxLines: 5000,
  lineHeight: 1.15,
  disabled: false,
  selected: false,
  textOptions: () => ({})
});

const emit = defineEmits<{
  (event: "blur"): void;
  (event: "focus"): void;
  (event: "update:value", value: string): void;
  (event: "enter", value: string): void;
  (event: "enter-edit-mode"): void;
  (event: "leave-edit-mode"): void;
  (event: "pointerdown", pointerEvent: PointerEvent & Handleable): void;
  <K extends keyof TextOptions>(event: "update:text-option", key: K, value: TextOptions[K]): void;
}>();

const mainElement = ref<HTMLElement>();
const editorContentElement = ref<InstanceType<typeof EditorContent>>();
const buttonElement = ref<HTMLElement>();

const netToolbarContext = useNetTextToolBarContext();
const textOptions = toRef(props, "textOptions");

const editing = ref(false);

// =======================
// VIEW

const onViewFocus = () => {
  emit("focus");
};

const onViewBlur = () => {
  emit("blur");
};

const MOVE_THRESHOLD = 12;
let lastClickTime = 0;

function onViewMousedown(mouseDownEvent: MouseEvent) {
  if (mouseDownEvent.button !== 0) return; // only left button
  if (mouseDownEvent.shiftKey) return;
  if (Date.now() - lastClickTime < 500) {
    // double click, ignore
    return;
  }
  lastClickTime = Date.now();
  const mouseDownX = mouseDownEvent.clientX;
  const mouseDownY = mouseDownEvent.clientY;
  mouseDownEvent.preventDefault();
  const pointerDownTime = Date.now();

  emit("pointerdown", mouseDownEvent as PointerEvent & Handleable);

  // catch the next click, testing if it is here, too.
  window.addEventListener("pointerdown", (pointerEvent: PointerEvent) => {
    if (pointerEvent.button !== 0) return; // only left button

    const timeBetweenClicks = Date.now() - pointerDownTime;
    if (timeBetweenClicks > 500) {
      // too late
      return;
    }

    const dx = Math.abs(pointerEvent.clientX - mouseDownX);
    const dy = Math.abs(pointerEvent.clientY - mouseDownY);
    if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
      console.log("moved too much, not editing", { dx, dy });
      return;
    }
    pointerEvent.preventDefault();

    window.addEventListener("pointerup", (upEvent: PointerEvent) => {
      // prevent focus change
      const dx = Math.abs(upEvent.clientX - mouseDownX);
      const dy = Math.abs(upEvent.clientY - mouseDownY);
      if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
        console.log("moved too much, not editing", { dx, dy });
        return;
      }
  
      // is click on the button?
      if (buttonElement.value?.contains(upEvent.target as Node)) {
        switchToEditMode();
        upEvent.preventDefault();
  
        setTimeout(() => {
          focus();
          insertCaretAtPoint(upEvent.clientX, upEvent.clientY, select);
        }, 0.5);
      }
    } , { once: true, capture: true });
  } , { once: true, capture: true });
}

function switchToEditMode() {
  if (props.disabled) return;
  editing.value = true;
  focus();
  netToolbarContext.setEditor(editor.value);
  netToolbarContext.setPosition(props.position ?? { x: 0, y: 0 });
  netToolbarContext.setTextOptions(textOptions);
  netToolbarContext.onUpdateOption((key, value) => {
    emit("update:text-option", key, value);
  });
  emit("enter-edit-mode");

  document.addEventListener("pointerdown", onClickOutside, {
    capture: true
  });
}

function onClickOutside(event: PointerEvent) {
  // If the click is outside the editor content, switch to view mode
  const path = event.composedPath();
  // Exception: do not close if clicking on the toolbar
  if (path.some((el) => {
    const htmlElement = el as HTMLElement | null;
    if (!htmlElement) return false;
    if (!htmlElement.classList) return false;
    if (htmlElement.classList.contains("net-text-toolbar__tooltip")) return true;
    return htmlElement.classList.contains("net-text-toolbar");
  })) {
    console.log("click on toolbar, not closing");
    return;
  }
  
  if (
    !editorContentElement.value?.$el.contains(event.target as Node) &&
    !buttonElement.value?.contains(event.target as Node)
  ) {
    console.log("click outside, leaving edit mode", event.target, path);
    leaveEditMode();
    event.preventDefault();
    event.stopPropagation();
  }
}

// =======================
// EDITOR

const EnterListener = Extension.create({
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        emit("enter", getText());
        if (!props.stylable) {
          // only leave edit mode on enter if not stylable, otherwise users would expect to be able to add line breaks with enter.
          leaveEditMode();
        }
        return true;
      }
    };
  }
});

const extensions = computed(() => (props.stylable ? fullExtensions : [...minimalExtensions, EnterListener]));

const editorOptions = computed((): Partial<EditorOptions> => {
  return {
    autofocus: false,
    editorProps: {
      attributes: {
        id: props.id,
        class: props.editorClass + " net-input-text__text formatted-text"
      }
    },
    parseOptions: {
      preserveWhitespace: true
    },
    content: props.value,
    extensions: extensions.value,
    onFocus: () => emit("focus"),
    onBlur: ({ event }) => onBlur(event),
    onUpdate: onUpdate
  };
});

const editor = useEditor(editorOptions.value);

watch(extensions, () => {
  if (editor.value) {
    console.log("update editor options!", editorOptions.value);
    editor.value.destroy();
    editor.value = new Editor(editorOptions.value);
  }
});

watch(
  () => props.value,
  (val: string) => {
    updateContent(val);
  },
  { immediate: true }
);

function updateContent(val: string) {
  if (editor.value == null) return;

  if (props.stylable) {
    if (editor.value.getHTML() !== val) {
      editor.value.chain().setContent(val, { parseOptions: { preserveWhitespace: true }}).run();
    }
  } else {
    if (editor.value.getText() !== val) {
      editor.value.chain().setContent(val, { parseOptions: { preserveWhitespace: true }}).run();
    }
  }
}

function getText() {
  if (editor.value == null) return "";
  if (isEditorEmpty()) return "";
  if (props.stylable) {
    return editor.value.getHTML();
  } else {
    return editor.value.getText();
  }
}

function onBlur(event: FocusEvent) {
  const relatedTargetElement = event.relatedTarget as HTMLElement | null;
  if (relatedTargetElement && (relatedTargetElement.closest(".net-text-toolbar") || relatedTargetElement.closest(".net-text-toolbar__tooltip"))) {
    // do not close if focusing the toolbar
    console.log("focusing toolbar, not closing");
    return;
  }
  leaveEditMode();
  emit("blur");
}

function leaveEditMode() {
  netToolbarContext.disconnect();
  document.removeEventListener("pointerdown", onClickOutside, {
    capture: true
  });
  updateContent(props.value);
  editing.value = false;
  emit("leave-edit-mode");
  mainElement?.value?.scroll(0, 0);
}

function focus() {
  if (editor.value == null) return;
  editor.value.chain().focus().run();
}
function blur() {
  if (editor.value == null) return;
  editor.value.chain().blur().run();
}

function select() {
  if (editor.value == null) return;
  editor.value.chain().selectAll().run();
}

function onUpdate(editorProps: EditorEvents["update"]) {
  const text = getText();
  if (text === props.value) return;
  emit("update:value", text);
}

function onTextPointerDown(event: PointerEvent & Handleable) {
  if (!editing.value) return;
  event.hasBeenHandled = true;
  event.handledBy = "NetInputText";
}

function isEditorEmpty() {
  if (editor?.value == null) return true;
  const doc = editor.value?.getJSON();
  if (doc.type !== "doc") return true;
  if (doc.content == null || doc.content.length === 0) return true;
  if (doc.content.length > 1) return false;
  if (doc.content[0].type !== "paragraph") return false;
  const paragraph = doc.content[0];
  if (paragraph.content == null || paragraph.content.length === 0) return true;
  return false;
}

// =======================
// EXPOSE

defineExpose({
  focus,
  switchToEditMode,
  select,
  blur,
  editing,
  editor,
  mainElement
});
</script>

<style lang="scss">
.net-input-text {
  color: inherit;
  overflow: hidden;
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content;
  line-height: v-bind("props.lineHeight");

  --min-height: calc(v-bind("props.lineHeight") * 1em);

  max-height: calc(v-bind("props.maxLines") * v-bind("props.lineHeight") * 1em);

  &__rendered {
    position: relative;
    all: unset;
    color: inherit;
    flex-direction: column;
    align-self: start;

    padding: 0;
    height: 100%;
    min-height: var(--min-height);
    min-width: 0;

    display: flex; // this is flex, because the inner text needs
    // to be at the top of the button and block moves
    // it down for some reason.

    &.is-empty {
      font-style: italic;
      opacity: 0.5;
      font-weight: 400;
      white-space: nowrap;
      height: 100%;
    }

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: -1px;
    }

    p {
      line-height: inherit;
    }
  }

  &__wrapped-preview {
    overflow: hidden;
    width: 100%;
    display: -webkit-box;
    -webkit-line-clamp: v-bind("props.maxLines");
    line-clamp: v-bind("props.maxLines");
    -webkit-box-orient: vertical;
    max-height: calc(
      v-bind("props.maxLines") * v-bind("props.lineHeight") * 1em + 0.1em
    );
    height: 100%;
    white-space: pre-wrap;
    white-space-collapse: break-spaces;
  }

  &__content {
    grid-area: 1 / 1 / 2 / 2;
    min-height: var(--min-height);
    min-width: 2px;
  }

  &__input {
    position: relative;
    min-height: var(--min-height);
  }

  &__text {
    font-size: inherit;
    font-family: inherit;
    line-height: inherit;
    min-height: 100%;

    & > :first-child {
      margin-top: 0;
    }

    p {
      line-height: inherit;
    }

    &:focus {
      outline: none;
    }
  }

  .ProseMirror {
    // IMPORTANT! TEXT EDITOR DEPENDS ON PRE-WRAP.
    white-space: pre-wrap;

    @supports (-moz-appearance: none) {
      // only firefox supports this fully
      // and it interferes with colors in chrome
      scrollbar-width: thin;
    }
  }
}
</style>
